import { useLocalStorageState, useUserScopedKey } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { DimensionToRender } from "../../RenderState/common";
import { ReactElement, useCallback, useState } from "react";
import { LiveTileContent } from "../LiveTileContent";
import { DaySchedule } from "./DaySchedule";
import { MagisterLiveInformationProvider } from "./provider";
import { WeekSchedule } from "./WeekSchedule";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto } from "client/DateAndTime/dateTimeUtils";

export type ScheduleProps = {
	tabId: Guid;
	connectionId: number;
	provider: MagisterLiveInformationProvider;
	dimensionToRender: DimensionToRender;
	destinationWindow: DestinationWindow;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

export const Schedule = (props: ScheduleProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		dimensionToRender,
		destinationWindow,
		timezone,
		addAnalyticsEvent
	} = props;

	const [activeDate, setActiveDate] = useState<Date>(new Date());

	const userScopedKey: string = useUserScopedKey("magister-live-tile-content:preferred-schedule-view");
	const [preferredScheduleKind, setPreferredScheduleKind] = useLocalStorageState<ScheduleKind>(
		userScopedKey,
		ScheduleKind.Week
	);

	const isWeekScheduleAvailable: boolean = dimensionToRender.dimension
		.columnSpan >= 6;

	const showTodayButton: boolean = dimensionToRender.dimension
		.columnSpan === 8;

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			// Cannot use toISOString as it returns Zulu (UTC) always and we want the local timezone
			// info on the backend
			const date: string = formatForDto(activeDate, timezone);

			navigateRelativeToEnvironment(
				`/Workspace/LiveInformation/Magister/${connectionId}/GoToSchedule?date=${encodeURIComponent(date)}`,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			activeDate,
			connectionId,
			destinationWindow,
			timezone,
			addAnalyticsEvent
		]
	);

	const renderSchedule = (): ReactElement => {
		const scheduleKind: ScheduleKind = isWeekScheduleAvailable
			? preferredScheduleKind
			: ScheduleKind.Day;

		switch (scheduleKind) {
			case ScheduleKind.Day:
				return (
					<DaySchedule
						tabId={tabId}
						connectionId={connectionId}
						provider={provider}
						date={activeDate}
						onDateChange={setActiveDate}
						isScheduleSelectionAvailable={isWeekScheduleAvailable}
						onChangeScheduleKind={setPreferredScheduleKind}
						isTodayButtonAvailable={showTodayButton}
						onNavigate={navigationHandler}
						timezone={timezone}
					/>
				);
			case ScheduleKind.Week:
				return (
					<WeekSchedule
						tabId={tabId}
						connectionId={connectionId}
						provider={provider}
						date={activeDate}
						onDateChange={setActiveDate}
						onChangeScheduleKind={setPreferredScheduleKind}
						isTodayButtonAvailable={showTodayButton}
						onNavigate={navigationHandler}
						timezone={timezone}
					/>
				);
			default:
				never(scheduleKind);
		}
	};

	return (
		<>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderSchedule()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_MySchedule")}
				onClick={navigationHandler}
			/>
		</>
	);
};
Schedule.displayName = "Schedule";

export enum ScheduleKind {
	Day = 0,
	Week = 1
}
